/* iter365 — DIL Explainer Modal.
 *
 * Compact patient-facing version of /dashboard/dil-audit. Triggered from
 * the "📊 Защо този DIL?" info button next to the DIL number anywhere
 * in the app (Diary, Dashboard, MetabolicPanel, MealPlanner header).
 *
 * Shows ONLY:
 *   1. Profile inputs that drive DIL (weight, goal, cycle phase, …)
 *   2. Step-by-step formula breakdown
 *   3. Scenarios at current weight (other goals)
 *   4. Formula + PMID reference
 *
 * Skips the admin-only "back-solve historical values" section.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X, FlaskConical, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";

export default function DilExplainerModal({ open, onClose }) {
  const { API, user } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const bg = language === "bg";

  const [loading, setLoading] = useState(false);
  const [audit, setAudit] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError("");
    setAudit(null);
    axios
      .get(`${API}/dil/audit/me`)
      .then(({ data }) => setAudit(data))
      .catch((err) => setError(err?.response?.data?.detail || err?.message || "Error"))
      .finally(() => setLoading(false));
  }, [open, API]);

  if (!open) return null;

  const cardCls = isDark
    ? "bg-[hsl(222,47%,11%)] border-white/10 text-gray-100"
    : "bg-white border-gray-200 text-gray-900";
  const accent = isDark ? "text-fuchsia-300" : "text-fuchsia-600";

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center p-3 bg-black/60 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      data-testid="dil-explainer-modal"
    >
      <div className={`w-full max-w-xl max-h-[90vh] flex flex-col rounded-2xl shadow-2xl border ${cardCls}`}>
        {/* Header */}
        <div className={`shrink-0 px-4 py-3 flex items-center justify-between border-b ${isDark ? "border-white/10" : "border-gray-100"}`}>
          <div className="flex items-center gap-2 min-w-0">
            <FlaskConical className="w-4 h-4 text-fuchsia-500 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm font-semibold">
                {bg ? "Защо този DIL?" : "Why this DIL?"}
              </div>
              <div className={`text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "Формула + входни данни + сценарии" : "Formula + inputs + scenarios"}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100" data-testid="dil-explainer-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto px-4 py-3 space-y-3">
          {loading && (
            <div className="flex items-center justify-center py-10 gap-2 text-fuchsia-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">{bg ? "Зареждам…" : "Loading…"}</span>
            </div>
          )}

          {!loading && error && (
            <div className={`rounded-lg p-3 text-[12px] ${isDark ? "bg-red-500/15 text-red-200" : "bg-red-50 text-red-700"}`}>
              {error}
            </div>
          )}

          {!loading && audit && (
            <>
              {/* Final DIL */}
              {audit.computed?.personalized_dil != null && (
                <div className={`p-3 rounded-lg text-center ${isDark ? "bg-fuchsia-500/10 ring-1 ring-fuchsia-500/30" : "bg-fuchsia-50 ring-1 ring-fuchsia-200"}`}>
                  <div className="text-[10px] uppercase tracking-wider opacity-70">
                    {bg ? "Личен DIL днес" : "Personal DIL today"}
                  </div>
                  <div className="text-3xl font-bold">
                    {audit.computed.personalized_dil}
                    <span className="text-base font-normal opacity-70 ml-1">g/{bg ? "ден" : "day"}</span>
                  </div>
                </div>
              )}

              {/* Profile inputs grid */}
              <section data-testid="explainer-inputs">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${accent}`}>
                  {bg ? "Входни данни" : "Inputs"}
                </div>
                <div className="grid grid-cols-3 gap-1.5 text-[11px]">
                  {[
                    ["weight_kg",     bg ? "Тегло" : "Weight",        "kg"],
                    ["height_cm",     bg ? "Височина" : "Height",     "cm"],
                    ["age",           bg ? "Възраст" : "Age",         ""],
                    ["sex",           bg ? "Пол" : "Sex",             ""],
                    ["goal",          bg ? "Цел" : "Goal",            ""],
                    ["cycle_day",     bg ? "Цикъл (ден)" : "Cycle day", ""],
                  ].map(([k, label, unit]) => {
                    const v = audit.inputs[k];
                    return (
                      <div key={k} className={`px-2 py-1 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                        <div className={`text-[9px] uppercase tracking-wider ${isDark ? "text-gray-400" : "text-gray-500"}`}>{label}</div>
                        <div className="font-mono">{v === null || v === undefined ? "—" : `${v}${unit ? " " + unit : ""}`}</div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Step-by-step breakdown */}
              <section data-testid="explainer-breakdown">
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${accent}`}>
                  {bg ? "Как се изчислява" : "How it's computed"}
                </div>
                <ol className="space-y-1 text-[11px]">
                  {(audit.breakdown || []).map((step, i) => (
                    <li key={i} className="flex items-start gap-1.5">
                      <span className={`shrink-0 mt-0.5 w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold ${isDark ? "bg-fuchsia-500/20 text-fuchsia-300" : "bg-fuchsia-100 text-fuchsia-700"}`}>{i + 1}</span>
                      <span className="flex-1 font-mono leading-relaxed">{step}</span>
                    </li>
                  ))}
                </ol>
              </section>

              {/* Scenarios */}
              {(audit.scenarios_at_current_weight || []).length > 0 && (
                <section data-testid="explainer-scenarios">
                  <div className={`text-[10px] uppercase tracking-wider font-bold mb-1.5 ${accent}`}>
                    {bg ? "Алтернативни цели при сегашното тегло" : "Alternative goals at current weight"}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className={`text-left ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          <th className="py-1 pr-2 font-semibold">{bg ? "Цел" : "Goal"}</th>
                          <th className="py-1 font-semibold text-right">DIL g/{bg ? "ден" : "day"}</th>
                        </tr>
                      </thead>
                      <tbody className="font-mono">
                        {audit.scenarios_at_current_weight.map((s) => (
                          <tr key={s.goal} className={audit.computed.effective_goal === s.goal ? (isDark ? "bg-fuchsia-500/15" : "bg-fuchsia-50") : ""}>
                            <td className="py-1 pr-2">{bg ? s.label_bg : s.goal}</td>
                            <td className="py-1 text-right font-bold">{s.personalized_dil}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              )}

              {/* Formula footer */}
              <section className={`pt-2 border-t ${isDark ? "border-white/10" : "border-gray-100"}`}>
                <div className={`text-[10px] uppercase tracking-wider font-bold mb-1 ${accent}`}>
                  {bg ? "Формула" : "Formula"}
                </div>
                <div className={`font-mono text-[10px] p-1.5 rounded ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                  DIL = bw × (carb/kg + 0.69 × protein/kg) × cycle × estrogen
                </div>
                <div className={`mt-1.5 text-[10px] ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                  PMID: {(audit.computed.pmid || []).join(", ")}
                  {audit.formula_doc_url && (
                    <>
                      {" · "}
                      <a href={audit.formula_doc_url} target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-0.5">
                        {bg ? "методология" : "methodology"} <ExternalLink className="w-2.5 h-2.5" />
                      </a>
                    </>
                  )}
                </div>
                {user?.role === "admin" && (
                  <Link
                    to="/dashboard/dil-audit"
                    onClick={() => onClose?.()}
                    className={`mt-2 inline-block text-[10px] font-semibold underline ${accent}`}
                    data-testid="explainer-link-admin"
                  >
                    {bg ? "Пълен admin audit →" : "Full admin audit →"}
                  </Link>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
